import React, {useEffect, useState} from 'react';
import {Col, Container, Row} from 'reactstrap';
import ABTest, {DebugMenu} from 'react-controlled-ab';
import {subscribeToCampaign} from 'react-controlled-ab/datalayers/vwo';

import {usePhaseState, usePhaseDispatch} from 'funnel-schedule/phase-context';
import {useUserState} from 'contexts/user';

import {SectionTriangle} from 'components/Elements/Section';
import {ImgCard} from 'components/Elements/ImgBullet';
import {
  critics,
  conversations,
  conversationsAd,
  impactKit,
  impactKitAd,
  experts,
  expertsAd,
  extendedInterviews
} from 'data/fungi.json';
import ReactMarkdown from 'react-markdown';
import {BonusOpen} from 'components/BonusList';
import GuaranteeBox from 'components/Guarantee';
import {FaqCard} from 'components/FaqList';

export default function SalesCopy({version = 'sales', salesCta = '', variant = null, children, ...props}) {
  const {bucket, email} = useUserState();
  const {
    phases: {
      default: defaultSchedule = {
        phase: 'before',
        launchStart: '',
        launchEnd: '',
        launchTitle: ''
      },
      sales: salesSchedule = {
        phase: 'during',
        salesEnd: '',
        isSalesDay: false
      },
      webinar: webinarSchedule = {
        phase: 'before'
      }
    }
  } = usePhaseState();

  // EX-30831 - Split Test - Join the Underground Movement Section Copy
  const [undergroundSection, setUndergroundSection] = useState(props.undergroundSection || 'control');
  useEffect(() => {
    subscribeToCampaign((variantIndex) => {
      switch (variantIndex) {
        case 1:
          setUndergroundSection('v1');
          break;
        case 2:
          setUndergroundSection('v2');
          break;
      }
    }, 638);
  });
  useEffect(() => {
    subscribeToCampaign((variantIndex) => {
      switch (variantIndex) {
        case 1:
          setUndergroundSection('v1');
          break;
        case 2:
          setUndergroundSection('v2');
          break;
      }
    }, 639);
  });

  // EX-30836 - Split Test - Does This Sound Like You Section Copy
  const [soundLikeYouSection, setSoundLikeYouSection] = useState(props.soundLikeYouSection || 'control');
  useEffect(() => {
    subscribeToCampaign((variantIndex) => {
      switch (variantIndex) {
        case 1:
          setSoundLikeYouSection('v1');
          break;
      }
    }, 640);
  });
  useEffect(() => {
    subscribeToCampaign((variantIndex) => {
      switch (variantIndex) {
        case 1:
          setSoundLikeYouSection('v1');
          break;
      }
    }, 641);
  });

  // EX-30837 - Split Test - Critic Reviews Copy
  const [criticReviewSection, setCriticReviewSection] = useState(props.criticReviewSection || 'control');
  useEffect(() => {
    subscribeToCampaign((variantIndex) => {
      switch (variantIndex) {
        case 1:
          setCriticReviewSection('v1');
          break;
      }
    }, 642);
  });
  useEffect(() => {
    subscribeToCampaign((variantIndex) => {
      switch (variantIndex) {
        case 1:
          setCriticReviewSection('v1');
          break;
      }
    }, 643);
  });

  return (
    <>
      <SectionTriangle id="ff-movement-1" color="white" type="bottom" innerClass="text-center pt-4">
        <Container>
          <Row>
            <Col>
              {version === 'screening' ? (
                <h3 className="section-heading mb-5">
                  Dig Deep into the Medicinal, Therapeutic, Environmental, and Culinary Powers of the Mushroom Kingdom
                  with The <i>Fantastic Fungi Impact Kit</i>
                </h3>
              ) : null}
              {
                {
                  control: <h2 className="section-heading">Join the Underground Movement to Create a New World</h2>,
                  v1: null
                }[undergroundSection]
              }
            </Col>
          </Row>
          {
            {
              control: (
                <>
                  <Row className="align-items-stretch justify-content-center text-center">
                    <Col xs="6" lg="3" className="d-flex flex-column mb-4">
                      <div className="mb-3">
                        <img src={require('static/icon/ff-saleslanding-icon-oil.png')} style={{maxHeight: '115px'}} />
                      </div>
                      <div className="my-auto text-ff-muted-orange text-accent">Toxic Waste</div>
                    </Col>
                    <Col xs="6" lg="3" className="d-flex flex-column mb-4">
                      <div className="mb-3">
                        <img
                          src={require('static/icon/ff-saleslanding-icon-disease.png')}
                          style={{maxHeight: '115px'}}
                        />
                      </div>
                      <div className="my-auto text-ff-muted-orange text-accent">Disease</div>
                    </Col>
                    <Col xs="6" lg="3" className="d-flex flex-column mb-4">
                      <div className="mb-3">
                        <img
                          src={require('static/icon/ff-saleslanding-icon-mental.png')}
                          style={{maxHeight: '115px'}}
                        />
                      </div>
                      <div className="my-auto text-ff-muted-orange text-accent">Mental Health Crisis</div>
                    </Col>
                    <Col xs="6" lg="3" className="d-flex flex-column mb-4">
                      <div className="mb-3">
                        <img
                          src={require('static/icon/ff-saleslanding-icon-climatechange.png')}
                          style={{maxHeight: '115px'}}
                        />
                      </div>
                      <div className="my-auto text-ff-muted-orange text-accent">Climate Change</div>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <p>
                        Disease, a mental health crisis, climate change, more and more toxic waste…and all{' '}
                        <br className="d-none d-xl-block" />
                        the dangers that come from a society that’s disconnected from the natural world.
                      </p>
                      <p>
                        But what if the answers to all these problems and more are already here, and have been all
                        along?
                      </p>
                      <p>
                        What if fungi can unlock the secrets to healing our lives, and our world — and all{' '}
                        <br className="d-none d-xl-block" />
                        we have to do is be willing to hear (and apply) what they have to tell us?
                      </p>
                      <p></p>
                    </Col>
                  </Row>
                </>
              ),
              v1: (
                <>
                  <Row>
                    <Col>
                      <h2 className="section-heading">Be on the cutting edge of the fungi revolution</h2>
                      <p className="balance-text">
                        For decades, certain fungi were banned, criminalized, or simply ignored — despite their immense
                        potential for therapeutic healing and regeneration.
                      </p>
                      <p className="balance-text">
                        Now, fungi are making headlines, thanks to promising new research showcasing their ability to
                        help us with some of the most pressing problems of our times.
                      </p>
                      <p className="balance-text">
                        From therapeutic benefits to addiction recovery to preventing environmental disasters, fungi are
                        at the forefront of some of the biggest breakthroughs our society has ever witnessed.
                      </p>
                      <p className="balance-text">
                        But most people have only just begun to skim the surface of what the fungi kingdom has to offer.
                      </p>
                      <h3 className="mt-0">
                        Calling all mushroom lovers and the fungi-curious who want to be included in the most important
                        conversations that are happening today, feed their soul, and have fun learning from the world’s
                        foremost experts…
                      </h3>
                      <p className="balance-text">
                        The <i>Fantastic Fungi</i> film shared the beauty of this kingdom with millions of people.
                      </p>
                      <p className="balance-text">
                        The breakthroughs that are happening right now, courtesy of fungi, aren’t just for the realm of
                        scientists, doctors, therapists, or professional mycologists.
                      </p>
                      <h4 className="mt-0">
                        Dig deeper and learn what’s new in the world of mushrooms since <i>Fantastic Fungi</i> came out
                        with the <i>Fantastic Fungi Impact Kit.</i>
                      </h4>
                    </Col>
                  </Row>
                </>
              ),
              v2: (
                <>
                  <Row>
                    <Col>
                      <h2 className="section-heading">Be on the cutting edge of the fungi revolution</h2>
                      <p className="balance-text">
                        For decades, it’s been taboo to talk about certain kinds of mushrooms. They’ve been banned,
                        feared, and outlawed, keeping millions of people from access to information that could help them
                        live a better life.
                      </p>
                      <p className="balance-text">
                        But now, new research coming from the most respected institutions is opening up possibilities
                        that didn’t exist just a few years ago.
                      </p>
                      <p className="balance-text">
                        People everywhere are asking, “Could mushrooms be the answer I’ve been looking for?”
                      </p>
                      <p className="balance-text">
                        This fungi renaissance is just beginning to explode, with mushrooms being celebrated and
                        explored in everything from therapy to cooking to medicinal uses to ecosystem restoration and
                        more.
                      </p>
                      <h3 className="mt-0">
                        We’re at a turning point in human consciousness — and fungi are at the forefront. Will you join
                        us?
                      </h3>
                      <h4 className="mt-0">
                        Dig deeper and learn what’s new in the world of mushrooms since <i>Fantastic Fungi</i> came out
                        with the <i>Fantastic Fungi Impact Kit.</i>
                      </h4>
                    </Col>
                  </Row>
                </>
              )
            }[undergroundSection]
          }
        </Container>
      </SectionTriangle>

      <SectionTriangle
        id="ff-renaissance"
        color="black"
        imgUrl={require('static/backgrounds/ff-saleslanding-image-body-joinrevolution-jesse-dodds-ZjDhntvNzAs-unsplash.jpg')}
        innerClass="d-flex flex-grow-1"
        offset="bottom"
      >
        <Container>
          <Row>
            <Col>
              <h2 className="section-only-heading text-center text-white text-shadow-dark">
                Join the Fungi Renaissance!
              </h2>
            </Col>
          </Row>
        </Container>
      </SectionTriangle>

      <SectionTriangle id="ff-need-1" color="ff-cream" type="both">
        <Container>
          <Row className="justify-content-center text-center">
            <Col lg="10">
              <h2 className="section-heading">
                {variant === 'ad' ? <>We Need Fungi & Fungi Need US</> : <>We Need Fungi & Fungi Need YOU</>}
              </h2>
              <p>
                Fantastic Fungi shows us that the future of the planet is at stake. <br className="d-none d-xl-block" />
                Mushrooms very well may be the key — if only we listen.
              </p>
              <p>
                Fungi really are magic. They can heal, repair, regenerate, and delight us.{' '}
                <br className="d-none d-xl-block" />
                And just like the web of mycelium connects life, fungi connect us <br className="d-none d-xl-block" />
                to one another in profoundly meaningful ways.
              </p>
              <p>
                {variant === 'ad' ? (
                  <>
                    Ever wished for an ally to bring our species closer <br className="d-none d-xl-block" />
                    to the mysteries of the natural world? This is it!
                  </>
                ) : (
                  <>
                    If you’ve ever wished for an ally to bring you closer <br className="d-none d-xl-block" />
                    to the mysteries of the natural world, this is it!
                  </>
                )}
              </p>
            </Col>
          </Row>
        </Container>
      </SectionTriangle>

      {
        {
          control: (
            <>
              <SectionTriangle id="ff-you-heading" color="white" type="bottom">
                <Container>
                  <Row>
                    <Col>
                      <h2 className="section-only-heading text-center">
                        {variant === 'ad' ? <>Get Inspired</> : <>Does any of this sound like you?</>}
                      </h2>
                    </Col>
                  </Row>
                </Container>
              </SectionTriangle>

              <SectionTriangle
                id="ff-you-1"
                className="section-triangle-image-lg-none"
                color="white"
                type="bottom"
                imgUrl={require('static/backgrounds/1283900775.jpg')}
                imgPosition="left"
              >
                <h3 className="section-heading">
                  {variant === 'ad' ? <>Mushrooms are health.</> : <>You love health.</>}
                </h3>
                <p>
                  {variant === 'ad' ? (
                    <>
                      Science is showing us how certain mushrooms are renowned for their health benefits and could
                      provide more energy, immune health, relaxation, and great sleep.
                    </>
                  ) : (
                    <>
                      Science is showing us how certain mushrooms are renowned for their health benefits and could
                      provide more energy, immune health, relaxation, and great sleep.
                    </>
                  )}
                </p>
              </SectionTriangle>

              <SectionTriangle
                id="ff-you-2"
                className="section-triangle-image-lg-none section-triangle-image-lg-none-offset"
                color="white"
                type="bottom"
                imgUrl={require('static/backgrounds/151812309.jpg')}
                imgPosition="right"
              >
                <h3 className="section-heading">
                  {variant === 'ad' ? <>Feel good. Feel free.</> : <>You want to feel good and free.</>}
                </h3>
                <p>
                  {variant === 'ad' ? (
                    <>
                      Ever tried healing routes like therapy, self-help books, journaling, and possibly medication? Want
                      to know everything possible about using therapeutic mushrooms to experience healing breakthroughs?
                      Are mushrooms for everyone? What is there to know about this burgeoning and exciting healing
                      modality?
                    </>
                  ) : (
                    <>
                      You’ve considered or even tried healing routes like therapy, self-help books, journaling, and
                      possibly medication. And you want to know everything you can about how people are successfully
                      using therapeutic mushrooms to experience healing breakthroughs. Are mushrooms for everyone? What
                      do you need to know about this new and exciting healing modality?
                    </>
                  )}
                </p>
              </SectionTriangle>
              <SectionTriangle
                id="ff-you-3"
                className="section-triangle-image-lg-none section-triangle-image-lg-none-offset"
                color="white"
                type="bottom"
                imgUrl={require('static/backgrounds/489150096.jpg')}
                imgPosition="left"
              >
                <h3 className="section-heading">
                  {variant === 'ad' ? (
                    <>Reliable research on the power of mushrooms.</>
                  ) : (
                    <>You want reliable research on how to use “magic” mushrooms with healing intent.</>
                  )}
                </h3>
                <p>
                  {variant === 'ad' ? (
                    <>
                      In the last few years, research and major news features are praising psychoactive forms of
                      mushrooms for everything from addiction treatment to spiritual connection to microdosing for
                      better brain performance. What are the experts saying? And how can we harness the latest
                      scientific research to expand our collective consciousness and heal — safely?
                    </>
                  ) : (
                    <>
                      In the last few years, research and major news features are praising psychoactive forms of
                      mushrooms for everything from addiction treatment to spiritual connection to microdosing for
                      better brain performance. What are the experts saying? And how can we harness the latest
                      scientific research to expand our collective consciousness and heal — safely?
                    </>
                  )}
                </p>
              </SectionTriangle>
              <SectionTriangle
                id="ff-you-4"
                className="section-triangle-image-lg-none section-triangle-image-lg-none-offset"
                color="white"
                type="bottom"
                imgUrl={require('static/backgrounds/878854774.jpg')}
                imgPosition="right"
              >
                <h3 className="section-heading">
                  {variant === 'ad' ? <>Climate change matters.</> : <>Climate change matters to you.</>}
                </h3>
                <p>
                  {variant === 'ad' ? (
                    <>
                      Fungi are being hailed as the greatest natural solution to climate change. They add carbon to the
                      soil, filter water, provide nutrients to plants that pump out oxygen, clean up oil spills and
                      pollution, and provide 100% compostable sources of packaging. Mushrooms can help make the world a
                      better place.
                    </>
                  ) : (
                    <>
                      Fungi are being hailed as the greatest natural solution to climate change. They add carbon to the
                      soil, filter water, provide nutrients to plants that pump out oxygen, clean up oil spills and
                      pollution, and provide 100% compostable sources of packaging. You want to know how mushrooms can
                      help make the world a better place.
                    </>
                  )}
                </p>
              </SectionTriangle>
              <SectionTriangle
                id="ff-you-5"
                className="section-triangle-image-lg-none section-triangle-image-lg-none-offset"
                color="white"
                type="bottom"
                imgUrl={require('static/backgrounds/mushroom-stew.jpg')}
                imgPosition="left"
              >
                <h3 className="section-heading">
                  {variant === 'ad' ? <>A foodie delight!</> : <>You’re a bit of a foodie!</>}
                </h3>
                <p>
                  {variant === 'ad' ? (
                    <>
                      For the love of wild food, foraging, cooking new dishes, and most of all, eating! Whether it’s
                      adding a few supporting mushrooms for a lip-smacking umami experience, or making them the star of
                      the show, get excited to lean into mushrooms for all their culinary delight.
                    </>
                  ) : (
                    <>
                      You love wild food, foraging, cooking new dishes, and most of all, eating! Whether it’s adding a
                      few supporting mushrooms for a lip-smacking umami experience, or making them the star of the show,
                      you’re excited to lean into mushrooms for all their culinary delight.
                    </>
                  )}
                </p>
              </SectionTriangle>
              <SectionTriangle
                id="ff-you-6"
                className="section-triangle-image-lg-none-offset"
                color="white"
                type="bottom"
                imgUrl={require('static/backgrounds/1301815523.jpg')}
                imgPosition="right"
              >
                <h3 className="section-heading">
                  {variant === 'ad' ? (
                    <>Eating a more plant-centered diet.</>
                  ) : (
                    <>You’re interested in eating a more plant-centered diet.</>
                  )}
                </h3>
                <p>
                  {variant === 'ad' ? (
                    <>
                      Mushrooms are one of the most savory ways to do just that — easily enjoy their outstanding health
                      benefits while contributing to significantly fewer greenhouse gas emissions.
                    </>
                  ) : (
                    <>
                      Mushrooms are one of the most savory ways to do just that — and you can enjoy the outstanding
                      health benefits while contributing to significantly fewer greenhouse gas emissions.
                    </>
                  )}
                </p>
              </SectionTriangle>
            </>
          ),
          v1: null
        }[soundLikeYouSection]
      }

      {
        {
          control: (
            <>
              <SectionTriangle
                id="ff-voices"
                color="black"
                imgUrl={require('static/backgrounds/ff-saleslanding-image-body-voices-marc-markstein-ibouKITXBFM-unsplash.jpg')}
                innerClass="d-flex flex-grow-1"
                offset="bottom"
              >
                <Container>
                  <Row className="justify-content-center">
                    <Col lg="10">
                      <h2 className="section-only-heading text-shadow-dark text-white text-center my-5">
                        The <i>Fantastic Fungi</i> film gathered dozens of leading voices to raise awareness and
                        celebrate the fungi renaissance.
                      </h2>
                    </Col>
                  </Row>
                </Container>
              </SectionTriangle>

              <SectionTriangle id="ff-critics" color="ff-cream" type="both">
                <Container>
                  <Row>
                    <Col>
                      <h4 className="section-heading text-center mb-5">
                        The <i>Fantastic Fungi</i> film has rocked audiences since it came out, with{' '}
                        <span className="text-highlight text-nowrap">100% ratings on Rotten Tomatoes</span> and rave
                        reviews from film critics.
                      </h4>
                    </Col>
                  </Row>
                  <Row className="d-flex align-items-stretch justify-content-center">
                    {critics.map((critic, i) => (
                      <Col md="10" lg="4" className="d-flex flex-grow-1 text-center mb-4 mb-lg-0">
                        <div className="d-flex flex-column background-white box-shadow-black-25 p-4">
                          <div className="my-auto">
                            <ReactMarkdown source={critic.text} />
                            {critic.image ? (
                              <img src={critic.image} style={{maxHeight: '50px'}} />
                            ) : (
                              <p className="mb-0">— {critic.name}</p>
                            )}
                          </div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </Container>
              </SectionTriangle>
            </>
          ),
          v1: (
            <>
              <SectionTriangle id="ff-critic" color="ff-gray" type="bottom">
                <Container>
                  <Row className="justify-content-center">
                    <Col lg="10">
                      <h2 className="section-only-heading text-white text-center my-5">
                        The <i>Fantastic Fungi</i> film has rocked audiences since it came out, with 100% ratings on
                        Rotten Tomatoes and rave reviews from film critics.
                      </h2>
                    </Col>
                  </Row>
                </Container>
              </SectionTriangle>
            </>
          )
        }[criticReviewSection]
      }

      <SectionTriangle
        id="ff-scratch"
        color="white"
        imgUrl={require('static/backgrounds/ff-saleslanding-image-body-divedeeper-forfeathering-nikola-radojcic--FHJb-mpjLs-unsplash.jpg')}
        imgPosition="left"
        type="bottom"
      >
        <h3 className="section-heading">
          But within the confines of a single film, we could only scratch the surface of this magnificent world that’s
          all around us.
        </h3>
        <p>
          That’s why we created a way to dive deeper into the underexplored kingdom of fungi — and experience the
          real-world magic.
        </p>
      </SectionTriangle>

      <SectionTriangle id="ff-intro-1" color="ff-gray" type="bottom" innerClass="text-center text-white">
        <Container>
          <Row>
            <Col>
              <h5 className="section-heading text-400">Introducing</h5>
              <h2 className="my-0">
                The <i>Fantastic Fungi Impact Kit</i>
              </h2>
            </Col>
          </Row>
        </Container>
      </SectionTriangle>

      <SectionTriangle id="ff-intro-2" color="ff-cream" type="bottom" innerClass="text-center">
        <Container>
          <Row>
            <Col>
              <img className="mb-5" src="https://cdn.foodrevolution.org/ff/FF-Product-Image-v20220628.png" />
              <h2 className="section-heading text-ff-gray mb-2">
                The Ultimate Collection of <i>Fantastic Fungi</i> Resources
              </h2>
              <h4 className="text-ff-gray mt-0">For Health, Healing, and Transformation</h4>
              <p>
                Brought to you by Louie and the <i>Fantastic Fungi</i> team, <br className="d-none d-xl-block" />
                in partnership with Food Revolution Network
              </p>
            </Col>
          </Row>
        </Container>
      </SectionTriangle>

      <SectionTriangle id="ff-miss" color="white" type="bottom">
        <Container>
          <Row>
            <Col>
              <h3 className="section-heading text-center text-ff-gray mb-0">
                Don’t Miss a Moment of This Urgent Wisdom
              </h3>
            </Col>
          </Row>
          <Row>
            <Col>
              {variant === 'ad' ? (
                <>
                  {conversationsAd.map((conversation, i) => (
                    <>
                      <h4 className="section-heading text-center my-5">{conversation.title}</h4>
                      <Row className="align-items-stretch justify-content-center">
                        {conversation.topics.map((topic, i) => (
                          <Col xs="12" md="6" className="d-flex flex-column mb-4">
                            <ImgCard img={topic.image}>{topic.name}</ImgCard>
                          </Col>
                        ))}
                      </Row>
                    </>
                  ))}
                </>
              ) : (
                <>
                  {conversations.map((conversation, i) => (
                    <>
                      <h4 className="section-heading text-center my-5">{conversation.title}</h4>
                      <Row className="align-items-stretch justify-content-center">
                        {conversation.topics.map((topic, i) => (
                          <Col xs="12" md="6" className="d-flex flex-column mb-4">
                            <ImgCard img={topic.image}>{topic.name}</ImgCard>
                          </Col>
                        ))}
                      </Row>
                    </>
                  ))}
                </>
              )}
            </Col>
          </Row>
        </Container>
      </SectionTriangle>

      <SectionTriangle id="ff-ik" color="ff-gray" type="bottom" innerClass="text-center text-white">
        <Container>
          <Row>
            <Col>
              <h3 className="section-only-heading">
                {version === 'screening' ? (
                  <>Uplift your health, the planet, and this urgent mission with the Impact Kit</>
                ) : (
                  <>
                    {variant === 'ad' ? (
                      <>
                        The <i>Fantastic Fungi Impact Kit</i>
                      </>
                    ) : (
                      <>Uplift your health, the planet, and this urgent mission with the Impact Kit</>
                    )}
                  </>
                )}
              </h3>
            </Col>
          </Row>
        </Container>
      </SectionTriangle>

      <SectionTriangle
        id="impact-cta"
        color="black"
        imgUrl={require('static/backgrounds/ff-saleslanding-image-header-backdrop-landon-parenteau-EEuDMqRYbx0-unsplash.jpg')}
        offset="bottom"
      >
        <Container>
          <Row className="justify-content-center">
            <Col lg="10" xl="8">
              {children}
            </Col>
          </Row>
        </Container>
      </SectionTriangle>

      <SectionTriangle id="ff-included" color="ff-cream" type="both">
        <Container>
          <Row>
            <Col>
              <h3 className="section-heading text-center">
                What’s Included In The <i>Fantastic Fungi Impact Kit</i>
              </h3>
            </Col>
          </Row>
          <Row className="d-flex align-items-stretch">
            {variant === 'ad'
              ? impactKitAd.map((impactKitItem, i) => (
                  <Col lg="6" className="d-flex flex-column mb-4">
                    <div className="background-white box-shadow-black-25 d-flex flex-column flex-grow-1">
                      <div className="impact-kit-image mb-3">
                        <img src={impactKitItem.image} />
                      </div>
                      <div className="p-4">
                        <ReactMarkdown className="text-h5 text-800" source={impactKitItem.name} />
                        <ReactMarkdown source={impactKitItem.text} />
                      </div>
                    </div>
                  </Col>
                ))
              : impactKit.map((impactKitItem, i) => (
                  <Col lg="6" className="d-flex flex-column mb-4">
                    <div className="background-white box-shadow-black-25 d-flex flex-column flex-grow-1">
                      <div className="impact-kit-image mb-3">
                        <img src={impactKitItem.image} />
                      </div>
                      <div className="p-4">
                        <ReactMarkdown className="text-h5 text-800" source={impactKitItem.name} />
                        <ReactMarkdown source={impactKitItem.text} />
                      </div>
                    </div>
                  </Col>
                ))}
          </Row>
        </Container>
      </SectionTriangle>

      <SectionTriangle id="ff-experts-1" color="ff-gray" type="bottom">
        <Container>
          <Row>
            <Col>
              <h2 className="section-only-heading text-center text-white">
                {variant === 'ad' ? (
                  <>
                    Get Immersed In An Incredible Vault of Wisdom With <i>Fantastic Fungi’s</i> Experts
                  </>
                ) : (
                  <>
                    Deepen Your Learning With <i>Fantastic Fungi’s</i> Experts
                  </>
                )}
              </h2>
            </Col>
          </Row>
        </Container>
      </SectionTriangle>

      <SectionTriangle id="ff-experts-2" color="white" type="bottom">
        <Container>
          <Row className="d-flex align-items-stretch justify-content-center text-center">
            {variant === 'ad'
              ? expertsAd.map((expert, i) => (
                  <Col xs="6" lg="4" className="expert-wrap d-flex flex-column px-3 mb-4">
                    <div className="mb-3">
                      <img className="rounded-circle" src={expert.image} style={{maxHeight: '150px'}} />
                    </div>
                    <div className="my-auto">
                      <h5 className="section-heading mb-2">{expert.name}</h5>
                      <ReactMarkdown className="text-small" source={expert.description} />
                    </div>
                  </Col>
                ))
              : experts.map((expert, i) => (
                  <Col xs="6" lg="4" className="expert-wrap d-flex flex-column px-3 mb-4">
                    <div className="mb-3">
                      <img className="rounded-circle" src={expert.image} style={{maxHeight: '150px'}} />
                    </div>
                    <div className="d-flex flex-column flex-grow-1">
                      <h5 className="section-heading mb-2">{expert.name}</h5>
                      <ReactMarkdown className="my-auto text-small" source={expert.description} />
                    </div>
                  </Col>
                ))}
          </Row>
        </Container>
      </SectionTriangle>

      <SectionTriangle id="ff-extended-1" color="ff-gray" type="bottom">
        <Container>
          <Row>
            <Col>
              <h2 className="section-only-heading text-center text-white">Unreleased & Extended Interviews</h2>
            </Col>
          </Row>
        </Container>
      </SectionTriangle>

      <SectionTriangle id="ff-extended-2" color="ff-cream" type="bottom">
        <Container>
          <Row>
            <Col>
              <p className="text-center mb-5">
                There’s only one way to get access to these additional interviews with the featured experts,{' '}
                <br className="d-none d-xl-block" />
                deleted scenes, and bonus features: The <i>Fantastic Fungi Impact Kit</i>
              </p>
            </Col>
          </Row>
          <Row className="d-flex align-items-stretch justify-content-center">
            {extendedInterviews.map((interview, i) => (
              <Col xs="6" lg="3" className="interview-wrap d-flex flex-grow-1 px-3 mb-4 mb-lg-0">
                <div className="d-flex flex-column background-white box-shadow-black-25">
                  <div className="px-1 pt-1 pb-0">
                    <img src={interview.image} />
                  </div>
                  <div className="p-3 my-auto">
                    <p className="text-small text-accent mb-1">{interview.name}</p>
                    <p className="text-small mb-0">{interview.type}</p>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </SectionTriangle>

      <SectionTriangle id="ff-bonus" color="white" type="bottom">
        <Container>
          <Row>
            <Col>
              <h2 className="section-heading text-ff-gray text-center">Bonus Gifts</h2>
              <BonusOpen />
            </Col>
          </Row>
        </Container>
      </SectionTriangle>

      <SectionTriangle
        id="impact-cta-2"
        color="black"
        imgUrl={require('static/backgrounds/ff-saleslanding-image-header-backdrop-landon-parenteau-EEuDMqRYbx0-unsplash.jpg')}
        offset="bottom"
      >
        <Container>
          <Row className="justify-content-center">
            <Col lg="10" xl="8">
              {children}
            </Col>
          </Row>
        </Container>
      </SectionTriangle>

      <SectionTriangle id="ff-guarantee" color="ff-cream" type="both">
        <Container>
          <Row>
            <Col>
              <GuaranteeBox
                title={
                  <>
                    60-Day Total Risk-Free <br className="d-none d-xl-block" />
                    Money-Back Guarantee
                  </>
                }
              >
                <p>
                  Take up to 60 days to soak up the entire Impact Kit. We guarantee that you and your family will love
                  what you learn.
                </p>
                <p>
                  Your satisfaction is important to us, so if for any reason you aren’t 100% happy with your purchase,
                  you have 60 days to request a prompt and total refund.
                </p>
                <p>
                  Just email us at <a href="mailto:support@foodrevolution.org">support@foodrevolution.org</a> or call us
                  at <a href="tel:831-824-4779">(831) 824-4779</a>.
                </p>
                <p>
                  PLUS, if for any reason you request a refund, the entire Impact Kit — everything that you’ve
                  downloaded — is still yours to keep.
                </p>
              </GuaranteeBox>
            </Col>
          </Row>
        </Container>
      </SectionTriangle>

      <SectionTriangle
        id="ff-trees"
        color="white"
        imgUrl={require('static/backgrounds/1322528652.jpg')}
        imgPosition="right"
        type="bottom"
        className="text-center text-lg-left"
      >
        <h4 className="section-heading">Want a better world? We do, too!</h4>
        <p className="text-h5">
          For every Impact Kit purchased, we make a donation to Trees for the Future, enabling them to plant another{' '}
          organic fruit or nut tree in a <br className="d-none d-xl-block" />
          low-income community.
        </p>
        <img src="https://cdn.foodrevolution.org/global/trees-logo-green.png" style={{maxHeight: '100px'}} />
      </SectionTriangle>

      <SectionTriangle
        id="ff-need-2"
        color="black"
        imgUrl={require('static/backgrounds/ff-saleslanding-image-body-needyou-karl-anderson-KH-WE85l3kc-unsplash.jpg')}
        bgImgPosition="center-bottom"
        offset="bottom"
      />

      <SectionTriangle id="ff-faq" color="ff-cream" type="both">
        <Container>
          <Row>
            <Col lg="4">
              <h3 className="section-heading text-center text-lg-right">Frequently Asked Questions</h3>
            </Col>
            <Col lg="8">
              <FaqCard />
            </Col>
          </Row>
        </Container>
      </SectionTriangle>

      <SectionTriangle
        id="ff-cta-3"
        color="black"
        imgUrl={require('static/backgrounds/ff-saleslanding-image-header-backdrop-landon-parenteau-EEuDMqRYbx0-unsplash.jpg')}
      >
        <Container>
          <Row className="justify-content-center">
            <Col lg="10" xl="8">
              {children}
            </Col>
          </Row>
        </Container>
      </SectionTriangle>
    </>
  );
}
